/**
 * Centralised URL builders for the management-plane-ui.
 *
 * Use these functions instead of writing URL strings inline so that
 * route shapes are maintained in one place.
 */

export const routes = {
  home: () => '/',
  dashboard: () => '/dashboard',
  infrastructure: () => '/infrastructure',
  deployments: () => '/deployments',
  workspaces: (releaseId?: string) =>
    releaseId ? `/workspaces?releaseId=${releaseId}` : '/workspaces',
  releases: () => '/releases',

  // Partners
  partners: () => '/partners',
  newPartner: () => '/partners/new',

  // Tenants
  customers: () => '/customers',
  tenant: (tenantId: string) => `/tenants/${tenantId}`,
  editTenant: (tenantId: string) => `/tenants/${tenantId}/edit`,
  newCustomer: (partnerId?: string) =>
    partnerId ? `/customers/new?partnerId=${partnerId}` : '/customers/new',

  // Users & roles (tenant-scoped)
  inviteMember: (tenantId: string) => `/tenants/${tenantId}/invite`,
  userDetail: (tenantId: string, userId: string) => `/tenants/${tenantId}/users/${userId}`,
  editUserAccess: (tenantId: string, userId: string) => `/tenants/${tenantId}/users/${userId}/edit`,

  // Platform Users & system roles (global, tenant-less Admin only) — a
  // "Platform User" is any tenant_id-NULL user (see SUN-408).
  platformUsers: () => '/platform-users',
  invitePlatformUser: () => '/platform-users/invite',
  platformUserDetail: (userId: string) => `/platform-users/users/${userId}`,
  editPlatformUserAccess: (userId: string) => `/platform-users/users/${userId}/edit`,
  systemRoles: () => '/system-roles',
  roleDetail: (roleId: string, tenantId?: string) =>
    tenantId ? `/roles/${roleId}?tenantId=${tenantId}` : `/roles/${roleId}`,
  newRole: (tenantId?: string) => (tenantId ? `/roles/new?tenantId=${tenantId}` : '/roles/new'),
  editRole: (roleId: string, tenantId?: string) =>
    tenantId ? `/roles/${roleId}/edit?tenantId=${tenantId}` : `/roles/${roleId}/edit`,

  // Deployments (scoped to tenant)
  newDeploymentGlobal: () => '/deployments/new',
  importOnPremCluster: () => '/deployments/import',
  newDeployment: (tenantId: string) => `/tenants/${tenantId}/deployments/new`,
  deployment: (tenantId: string, deploymentId: string) =>
    `/tenants/${tenantId}/deployments/${deploymentId}`,
  deploymentWorkspace: (tenantId: string, deploymentId: string, workspaceId: string) =>
    `/tenants/${tenantId}/deployments/${deploymentId}/workspaces/${workspaceId}`,

  // Workspaces
  newWorkspaceGlobal: () => '/workspaces/new',
  newWorkspace: (tenantId: string, deploymentId: string) =>
    `/tenants/${tenantId}/deployments/${deploymentId}/workspaces/new`,

  // Other
  chat: () => '/chat',
  help: () => '/help',
  settings: () => '/settings',
  preferences: () => '/preferences',
  callback: () => '/callback',
  signedOut: () => '/signed-out',
  status: () => '/status',
} as const;
